import React, { useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { 
  Edit3, 
  Save, 
  X, 
  Download, 
  FileText,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import ContentNavigationBar from './ContentNavigationBar';
import './BoardDeckCenterPanel.css';

const BoardDeckCenterPanel = ({
  generatedContent,
  editedContent,
  setEditedContent,
  isEditing,
  setIsEditing,
  onEdit,
  onSave,
  onCancel,
  onDownload,
  error,
  success,
  activeSection,
  onSectionClick
}) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isEditing]);

  if (!generatedContent) {
    return (
      <div className="board-deck-center-panel">
        <div className="empty-state">
          <FileText size={48} className="empty-icon" />
          <h3>No content generated yet</h3>
          <p>Configure your settings and click "Generate Board Deck" to create your presentation narrative.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="board-deck-center-panel">
      {/* Content Header */}
      <div className="content-header">
        <h3 className="content-title">Generated Content</h3>
        <div className="content-actions">
          <button
            className="action-btn"
            onClick={isEditing ? onSave : onEdit}
          >
            {isEditing ? <Save size={16} /> : <Edit3 size={16} />}
            {isEditing ? 'Save' : 'Edit'}
          </button>
          {isEditing && (
            <button
              className="action-btn cancel"
              onClick={onCancel}
            >
              <X size={16} />
              Cancel
            </button>
          )}
          <button
            className="action-btn"
            onClick={onDownload}
          >
            <Download size={16} />
            Download
          </button>
        </div>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="status-message error">
          <AlertCircle className="status-icon" />
          <span>{error}</span>
        </div>
      )}
      
      {success && (
        <div className="status-message success">
          <CheckCircle className="status-icon" />
          <span>{success}</span>
        </div>
      )}

      {/* Content Display */}
      <div className="content-display">
        {isEditing ? (
          <textarea
            ref={textareaRef}
            className="content-editor"
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            placeholder="Generated content will appear here..."
          />
        ) : (
          <div className="markdown-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children, ...props }) => {
                  const text = children.toString().replace(/\*\*/g, '').replace(/\*/g, '').trim();
                  const sectionId = text.toLowerCase().replace(/[^a-z0-9]/g, '-');
                  return <h1 id={sectionId} className="markdown-h1" {...props}>{children}</h1>;
                },
                h2: ({ children, ...props }) => {
                  const text = children.toString().replace(/\*\*/g, '').replace(/\*/g, '').trim();
                  const sectionId = text.toLowerCase().replace(/[^a-z0-9]/g, '-');
                  return <h2 id={sectionId} className="markdown-h2" {...props}>{children}</h2>;
                },
                h3: ({ children, ...props }) => {
                  const text = children.toString().replace(/\*\*/g, '').replace(/\*/g, '').trim();
                  const sectionId = text.toLowerCase().replace(/[^a-z0-9]/g, '-');
                  return <h3 id={sectionId} className="markdown-h3" {...props}>{children}</h3>;
                },
                h4: (props) => <h4 className="markdown-h4" {...props} />,
                h5: (props) => <h5 className="markdown-h5" {...props} />,
                h6: (props) => <h6 className="markdown-h6" {...props} />,
                p: (props) => <p className="markdown-p" {...props} />,
                strong: (props) => <strong className="markdown-strong" {...props} />,
                em: (props) => <em className="markdown-em" {...props} />,
                ul: (props) => <ul className="markdown-ul" {...props} />,
                ol: (props) => <ol className="markdown-ol" {...props} />,
                li: (props) => <li className="markdown-li" {...props} />,
                table: (props) => <table className="markdown-table" {...props} />,
                thead: (props) => <thead className="markdown-thead" {...props} />,
                tbody: (props) => <tbody className="markdown-tbody" {...props} />,
                tr: (props) => <tr className="markdown-tr" {...props} />,
                th: (props) => <th className="markdown-th" {...props} />,
                td: (props) => <td className="markdown-td" {...props} />,
                code: (props) => <code className="markdown-code" {...props} />,
                pre: (props) => <pre className="markdown-pre" {...props} />,
                blockquote: (props) => <blockquote className="markdown-blockquote" {...props} />,
                hr: (props) => <hr className="markdown-hr" {...props} />,
              }}
            >
              {generatedContent}
            </ReactMarkdown>
          </div>
        )}
      </div>
      
      {/* Content Navigation Bar */}
      <ContentNavigationBar
        content={generatedContent}
        activeSection={activeSection}
        onSectionClick={onSectionClick}
      />
    </div>
  );
};

export default BoardDeckCenterPanel;

